import { Search } from "lucide-react";
import { WhatsappConversation } from "../types";
import { cn } from "@/lib/utils";

type ConversationListProps = {
  conversations: WhatsappConversation[];
  selectedConversationId?: string | null;
  search: string;
  isLoading?: boolean;
  errorMessage?: string | null;
  onSearchChange: (value: string) => void;
  onSelectConversation: (conversation: WhatsappConversation) => void;
};

export function ConversationList({
  conversations,
  selectedConversationId,
  search,
  isLoading,
  errorMessage,
  onSearchChange,
  onSelectConversation,
}: ConversationListProps) {
  return (
    <div className="flex h-full min-w-0 flex-col overflow-hidden rounded-[10px] border border-[#e5e7eb] bg-white dark:border-slate-700 dark:bg-slate-900">
      <div className="border-b border-slate-200 p-3 dark:border-slate-700">
        <div className="flex min-w-0 items-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
          <Search className="h-4 w-4" />
          <input
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Buscar por nome ou telefone"
            className="w-full min-w-0 bg-transparent text-slate-900 outline-none placeholder:text-slate-400 dark:text-slate-100 dark:placeholder:text-slate-500"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <p className="p-4 text-sm text-slate-500 dark:text-slate-400">Carregando conversas...</p>
        ) : errorMessage ? (
          <p className="p-4 text-sm text-rose-600">{errorMessage}</p>
        ) : !conversations.length ? (
          <p className="p-4 text-sm text-slate-500 dark:text-slate-400">Nenhuma conversa encontrada.</p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-700">
            {conversations.map((conversation) => {
              const selected = conversation.id === selectedConversationId;
              const unread = Number(conversation.unreadCount ?? 0);
              return (
                <li key={conversation.id}>
                  <button
                    type="button"
                    onClick={() => onSelectConversation(conversation)}
                    className={cn(
                      "w-full px-3 py-3 text-left transition hover:bg-slate-50",
                      "dark:hover:bg-slate-800",
                      selected && "bg-emerald-50 dark:bg-emerald-950/45",
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {conversation.contactName?.trim() || formatPhone(conversation.contactPhone)}
                        </p>
                        <p className="truncate text-xs text-slate-500 dark:text-slate-300">
                          {conversation.contactName?.trim()
                            ? formatPhone(conversation.contactPhone)
                            : "Contato sem nome"}
                        </p>
                      </div>
                      <span className="shrink-0 text-[11px] text-slate-500 dark:text-slate-400">
                        {formatShortDate(conversation.lastMessageAt)}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <p className="min-w-0 truncate text-xs text-slate-600 dark:text-slate-300">
                        {conversation.lastMessagePreview?.trim() || "Sem mensagens"}
                      </p>
                      {unread > 0 ? (
                        <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-emerald-600 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                          {unread}
                        </span>
                      ) : null}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function formatPhone(phone: string): string {
  const digits = String(phone ?? "").replace(/\D/g, "");
  if (digits.length < 12) {
    return phone;
  }
  const country = digits.slice(0, 2);
  const ddd = digits.slice(2, 4);
  const start = digits.slice(4, 9);
  const end = digits.slice(9);
  return `+${country} (${ddd}) ${start}-${end}`;
}

function formatShortDate(value?: string | null): string {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}
